import { Component, OnDestroy, ViewEncapsulation, OnInit, inject } from '@angular/core';
import { LoadingController, ModalController } from '@ionic/angular';
import { DateTime, DateTime as LuxonDateTime } from 'luxon';
import { BookJob } from 'src/providers/book-job/book-job';
import { Booking } from 'src/providers/booking/booking';
import { Communication } from 'src/providers/communication/communication';
import { Card } from 'src/providers/card/card';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Client } from 'src/providers/client/client';
import { CurrentAddress } from 'src/providers/addresses/current-address';
import { Me } from 'src/providers/me/me';
import { Pros } from 'src/providers/pros/pros';
import { Requests } from 'src/providers/requests/requests';
import { Schedule } from 'src/providers/schedule/schedule';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Loading } from 'src/shared/components/loading/loading';
import { AddressModel, PlanModel } from 'src/models/address.model';
import { Filter, ScheduleCardModel, SelectedFilters } from 'src/models/schedule.model';
import { TidySelectNumberValueModel } from 'src/models/tidy-select-item.model';
import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';
import { Util } from 'src/shared/util/util';
import { Logger } from 'src/providers/logger';
import { WindowService } from 'src/shared/providers/window.service';
import { ClientSettingsModel } from 'src/models/client.model';
import { scrollContentWidth } from 'src/shared/components/scroll-wrapper/scroll-wrapper.component';
import { AvailabilityRequestResponse, ClickAction, Job, onClickActionMonthlyView, OwnerBlock, Period, Properties } from 'src/models/schedule-weekly-view.model';
import { Menu } from 'src/providers/menu/menu';
import { Events } from 'src/providers/events/events';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { firstValueFrom, forkJoin, from, Subscription } from 'rxjs';
import { BookService } from 'src/shared/services/book.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { InquiriesService } from 'src/shared/services/inquiries.service';
import { effectiveInquiryStatus } from 'src/shared/util/inquiry-status';
import { PastPrivateJobPage } from '../more/past-private-job/past-private-job';
import { PastJobPage } from '../more/past-job/past-job';
import { JobPage } from './job/job';
import { ReservationPage } from '../reservation/reservation';
import { CallProPage } from 'src/pages/call-pro/call-pro';
import { PlansPage } from 'src/pages/plans/plans';
import { ReportIssue } from 'src/providers/report-issue/report-issue';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { SummaryOfDayPage } from './summary-of-day/summary-of-day';
import { ActivatedRoute } from '@angular/router';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { OnboardingDummyDataService } from 'src/shared/services/onboarding-dummy-data.service';
import { BREAK_LARGE_RESOLUTION } from 'android/app/src/constants/app.contants';
import { RequestProCounterPage } from 'src/pages/request-pro-counter/request-pro-counter';
import { PendingProRequestPage } from 'src/pages/pending-pro-request/pending-pro-request';
import { ScheduleListPage } from 'src/pages/schedule/schedule-list/schedule-list';
import { AddReservationPage } from 'src/pages/add-reservation/add-reservation';
import { AddOwnerBlockPage } from 'src/pages/add-owner-block/add-owner-block';
import { OwnerBlocksService } from 'src/shared/services/owner-blocks.service';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

@Component({
  templateUrl: 'schedule.html',
  styleUrls: ['./schedule.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})

export class SchedulePage implements OnDestroy, OnInit {

  allTableStartEndDays: any;
  addressFilter: TidySelectNumberValueModel[];
  addressFilterLoaded: boolean;
  addressId = null;
  addressName: string;
  addressResponse: AddressModel[];
  addressRegionId: number;
  cards: ScheduleCardModel[];
  cardsLoaded: boolean;
  clientName: string;
  contentHeight: number;
  errorMessage: string;
  showContactConcierge = false;
  settings: ClientSettingsModel;
  hasJobs: boolean;
  hasPhone: boolean;
  hasPrivatePro: boolean;
  hasRequests: boolean;
  hasOnlyCrafRequests: boolean;
  hasOnlyReservations: boolean;
  hasJobsAtOnlyAddress: boolean;
  hasAllOneTimePlans: boolean;
  isAllAddressesSelected: boolean;
  initialAddressFilter: number[] = [];
  isRentalClient: boolean;
  automaticBookingEnabled: boolean;
  loadingNotifications: boolean;
  hasCreditCard: boolean;
  nextAvailableDate: string;
  plans: PlanModel[];
  waterfallSettings: any;
  twoCoops = false;
  notificationHistory: any;
  hasAddresses: boolean;
  hasReservations: boolean;
  desktopWidthContent: string = scrollContentWidth.PORTRAIT;
  daysOfWeek: string[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  selectedPeriodRange = {
    startDate: LuxonDateTime.local().toISODate(),
    endDate: LuxonDateTime.local().plus({ days: 6 }).toISODate()
  }
  isScheduleDataLoaded = false;

  /**
   * Mirrors the weekly view's readinessChange output. The calendar skeleton
   * stays mounted until BOTH the schedule data is loaded and the weekly view
   * has settled and revealed its grid, so the skeleton survives the
   * data-arrived handoff (its float-in wave never gets cut by an instance
   * swap). Resets via readinessChange whenever a recreated weekly view
   * starts pending again (e.g. week navigation refetches).
   */
  isWeeklyViewReady = false;

  propertiesFilters: SelectedFilters = {
    status: [],
    type: [],
    service: [],
    address: [],
    propertyName: [],
    pro: [],
  }
  propertiesSearch = '';
  phoneNumber: any;
  routeReused: boolean;
  selectedProsIds: number[] = [];
  showLoadingSpinner: boolean;
  panelClosedSubscription: Subscription;
  scheduleViewMode: 'MONTHLY' | 'WEEK_MON_SUN' | 'SEVEN_DAYS' | 'DAY' | 'LIST';
  form: UntypedFormGroup;
  isLoadingSchedule = true;
  loaded: boolean;
  filters: {
    homekeeper_ids?: number[];
    address_ids?: number[];
  } = {};
  showAboutThisPage: boolean;
  hasProperties: boolean;
  reloadSchedulePageSubscription$: Subscription;
  private resizeHandler: () => void;
  // Guards against a stale owner-blocks fetch landing after the user
  // navigated to another period or view mode.
  private ownerBlocksFetchToken = 0;
  // In-flight owner-blocks fetch for the current period, started alongside
  // the schedule-calendar requests. Resolves `null` when the view doesn't use
  // blocks.
  private ownerBlocksPromise: Promise<Map<number, OwnerBlock[]> | null> = Promise.resolve(null);
  // Set when the fetch missed the render deadline, so it attaches its result
  // itself instead of the (already-completed) render waiting on it.
  private ownerBlocksDeadlinePassed = false;
  // The period and view the blocks on screen were fetched for, and the ones the
  // in-flight fetch is for. When they match (a reload after blocking or
  // unblocking dates), the blocks on screen stay until the new ones land.
  private shownOwnerBlocksKey: string | null = null;
  private fetchingOwnerBlocksKey: string | null = null;
  // How long first paint will wait on the blocks before rendering without
  // them. Long enough to cover a normal round-trip, short enough that a hung
  // request can't strand the schedule behind its skeleton.
  private readonly OWNER_BLOCKS_RENDER_DEADLINE_MS = 1500;
  // Pending inquiries per address (all dates, from GET /inquiries) — stamped
  // onto parsed properties for the left-column "N inquiries" row and the
  // inquiry-first sort. Fetched once per page load; period navigation reuses it.
  private inquiryCountsByAddress: Record<number, number> = {};
  private pendingInquiryCountsPromise: Promise<Record<number, number>> | null = null;

  constructor(
    private bookingService: Booking,
    private bookJobService: BookJob,
    private card: Card,
    public communication: Communication,
    private client: Client,
    private me: Me,
    public schedule: Schedule,
    private navCtrl: CustomNavController,
    private util: Util,
    private modalCtrl: ModalController,
    private pros: Pros,
    public  requests: Requests,
    private route: ActivatedRoute,
    public windowService: WindowService,
    private currentAddress: CurrentAddress,
    private logger: Logger,
    private storage: TidyStorage,
    private loadingCtrl: LoadingController,
    public menu: Menu,
    private events: Events,
    private reportIssue: ReportIssue,
    private rightSidePanelService: RightSidePanelService,
    private onboardingDummyData: OnboardingDummyDataService,
    private aboutThisPageService: AboutThisPageService,
    private onboardingProvider: OnboardingProvider,
    private bookService: BookService,
    private demoBlock: DemoBlockService,
    private ownerBlocksService: OwnerBlocksService,
    private inquiriesService: InquiriesService
  ) {
    this.storage.delete('proId');
    this.storage.delete('chatRoomKey');
    this.reloadSchedulePageSubscription$ = this.schedule.getReloadSchedulePage().subscribe(() => {
      this.loadPageAfterPanelClosed();
    });
    this.form = new UntypedFormGroup({
      scheduleViewMode: new UntypedFormControl(''),
    });
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('schedule');
    localStorage.setItem('showAboutThisPageForSchedule', this.showAboutThisPage.toString());
  }

  loadPageAfterPanelClosed() {
    // Schedule edits (e.g. blocking/unblocking dates) must refresh MONTHLY
    // too, so the flag is checked before the monthly early-return.
    const madeUpdateToSchedule = localStorage.getItem('madeUpdateToSchedule') === 'true';
    const selectCategoryOpened = localStorage.getItem('select-category-opened');
    if (selectCategoryOpened !== 'true' && this.scheduleViewMode === 'MONTHLY' && !madeUpdateToSchedule) {
      return;
    }
    localStorage.setItem('select-category-opened', 'false');
    const getAddressesFromBackend = this.navCtrl.getParam('getAddressesFromBackend');
    const route = this.route.snapshot.url.map(segment => segment.path).join('/');
    let requestMadeInsideRightSidePanel = this.rightSidePanelService.requestMadeInsideRightSidePanel$.getValue();
    if (madeUpdateToSchedule) {
      requestMadeInsideRightSidePanel = true;
      localStorage.removeItem('madeUpdateToSchedule');
    }
    if (route.includes('schedule') && requestMadeInsideRightSidePanel) {
      this.storage.save('skipSortingProperties', true);
      this.getPageData(getAddressesFromBackend);
    }
  }

  async ngOnInit() {
    try {
      this.isLoadingSchedule = true;
      const shouldReloadSchedule = await this.storage.retrieve('shouldReloadSchedule');
      if (shouldReloadSchedule) {
        this.storage.delete('shouldReloadSchedule');
        window.location.reload();
      }
      await this.loadPage();
      this.resizeHandler = () => {
        const isDesktopResolution = window.innerWidth > BREAK_LARGE_RESOLUTION;
        if (!isDesktopResolution) {
          this.scheduleViewMode = 'SEVEN_DAYS';
          this.storage.save('scheduleViewMode', this.scheduleViewMode);
        }
      };
      window.addEventListener('resize', this.resizeHandler);
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err?.message;
    }
  }

  async loadPage(showLoadingSpinner = false, setStoredRoute = true) {
    if (showLoadingSpinner) {
      this.showLoadingSpinner = true;
      this.cleanup();
    }
    // Re-fetch pending inquiries each page load (counts change when the host
    // responds); started here so it runs alongside the calendar requests.
    this.pendingInquiryCountsPromise = null;
    void this.fetchPendingInquiryCounts();
    if (!this.panelClosedSubscription || this.panelClosedSubscription.closed) {
      this.panelClosedSubscription = this.rightSidePanelService.afterPanelClosed().subscribe(() => {
        this.loadPageAfterPanelClosed();
      });
    }
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('schedule');
    this.scheduleViewMode = await this.storage.retrieve('scheduleViewMode') || 'SEVEN_DAYS';
    if (this.scheduleViewMode !== 'MONTHLY' && this.scheduleViewMode !== 'WEEK_MON_SUN' && this.scheduleViewMode !== 'SEVEN_DAYS' && this.scheduleViewMode !== 'DAY' && this.scheduleViewMode !== 'LIST') {
      this.scheduleViewMode = 'WEEK_MON_SUN';
      await this.storage.save('scheduleViewMode', this.scheduleViewMode);
    }
    await this.getInitialSelectedPeriodRange();
    this.errorMessage = '';
    const shouldClearLocalStorage = !this.windowService.isDesktopRes || showLoadingSpinner;
    await this.getInitialData(shouldClearLocalStorage);
    await this.getAditionalData();
    this.showLoadingSpinner = false;
    this.loaded = true;
    if (this.windowService.isDesktopRes && setStoredRoute) {
      this.storage.setStoredRoute('/schedule').then(async () => {
        this.routeReused = true;
        await this.loadPage(true);
      });
    }
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.cards = [];
    this.schedule.properties = [];
    this.schedule.imutableProperties = [];
    this.isScheduleDataLoaded = false;
    this.isWeeklyViewReady = false;
    this.loadPage(true, false);
  }

  ngOnDestroy() {
    // Unsubscribed here rather than in cleanup(): loadPage(true) runs cleanup()
    // on every desktop load and nothing re-subscribes this, so unblocking dates
    // from the schedule's popover would stop refreshing the schedule.
    if (this.reloadSchedulePageSubscription$) {
      this.reloadSchedulePageSubscription$.unsubscribe();
    }
    this.cleanup();
  }

  cleanup() {
    if (this.panelClosedSubscription) {
      this.panelClosedSubscription.unsubscribe();
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  onSearchChanges(event: any): void {
    this.propertiesSearch = event.search.toLowerCase();
    this.updateTableFilters()
  }

  onFilterChanges(event: Filter): void {
    this.propertiesFilters = event.selectedFilters;
    this.saveFiltersToStorage();
    if (event.lastSelectedFilter === 'pro') {
      this.filters.homekeeper_ids = event.selectedFilters.pro
      this.getScheduleData(this.selectedPeriodRange, this.addressFilter);
      return
    }
    if (event.lastSelectedFilter === 'address' || event.lastSelectedFilter === 'propertyName') {
      this.filters.address_ids = [...event.selectedFilters.address, ...event.selectedFilters.propertyName]
      this.getScheduleData(this.selectedPeriodRange, this.addressFilter);
      return
    }
    this.updateTableFilters()
  }

  private saveFiltersToStorage(): void {
    localStorage.setItem('scheduleFilters', JSON.stringify(this.propertiesFilters));
  }

  private restoreFiltersFromStorage(): void {
    const savedFilters = localStorage.getItem('scheduleFilters');
    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters) as SelectedFilters;
        this.propertiesFilters = parsedFilters;
        this.initialAddressFilter = parsedFilters.address || [];
        if (parsedFilters.pro?.length) {
          this.filters.homekeeper_ids = parsedFilters.pro;
        }
        if (parsedFilters.address?.length || parsedFilters.propertyName?.length) {
          this.filters.address_ids = [...(parsedFilters.address || []), ...(parsedFilters.propertyName || [])];
        }
      } catch (e) {
        localStorage.removeItem('scheduleFilters');
      }
    }
  }

  updateTableFilters(): void {
    const filteredProperties = this.schedule.updateTableFilter(this.schedule.imutableProperties, this.propertiesFilters)
    this.schedule.properties = this.schedule.updateTableSearch(filteredProperties, this.propertiesSearch)
    if (this.selectedProsIds.length > 0) {
      this.schedule.properties = this.schedule.properties.filter((property) => property.filteredJobs.length > 0 || property.filteredReservations.length > 0)
    }
  }

  async changeScheduleViewMode(event: any): Promise<void> {
    this.schedule.properties = [];
    this.schedule.imutableProperties = [];
    this.isScheduleDataLoaded = false;
    this.isWeeklyViewReady = false;
    this.isLoadingSchedule = true;
    this.scheduleViewMode = event;
    this.storage.save('scheduleViewMode', event);
    await this.getInitialSelectedPeriodRange(true); // Pass true to indicate view mode switch
    this.getScheduleData(this.selectedPeriodRange, this.addressFilter);
  }

  onDateRangeChange(event: any): void {
    // Handle date range changes for LIST view
    if (this.scheduleViewMode === 'LIST') {
      let startDate: string;
      let endDate: string;

      if (event.type === 'string') {
        // Handle preset date ranges
        const today = LuxonDateTime.local();
        switch (event.value) {
          case 'today':
            startDate = today.toISODate();
            endDate = today.toISODate();
            break;
          case 'last_week':
            startDate = today.minus({ weeks: 1 }).startOf('week').toISODate();
            endDate = today.minus({ weeks: 1 }).endOf('week').toISODate();
            break;
          case 'last_month':
            startDate = today.minus({ months: 1 }).startOf('month').toISODate();
            endDate = today.minus({ months: 1 }).endOf('month').toISODate();
            break;
          default:
            startDate = today.startOf('month').toISODate();
            endDate = today.endOf('month').toISODate();
        }
      } else if (event.type === 'custom' && event.value) {
        // Handle custom date range
        startDate = event.value.start;
        endDate = event.value.end;
      }

      if (startDate && endDate) {
        // Use the full date range for LIST view
        this.selectedPeriodRange = { startDate, endDate };
        this.isScheduleDataLoaded = false;
        this.isWeeklyViewReady = false;
        this.getScheduleData(this.selectedPeriodRange, this.addressFilter);
      }
    }
  }

  async getInitialSelectedPeriodRange(isViewModeSwitch = false): Promise<void> {
    if (this.showAboutThisPage) {
      this.selectedPeriodRange = {
        startDate: '2025-03-25',
        endDate: '2025-03-31'
      }
      return;
    }
    this.daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const today = LuxonDateTime.local();
    let startDate, endDate;

    if (this.scheduleViewMode === 'LIST') {
      if (isViewModeSwitch) {
        const savedDateRangeOption = await this.storage.retrieve('listViewDateRangeOption');
        const savedCustomDateRange = await this.storage.retrieve('listViewCustomDateRange');

        if (savedDateRangeOption && savedDateRangeOption !== 'this_month') {
          if (savedDateRangeOption === 'custom' && savedCustomDateRange) {
            startDate = savedCustomDateRange.startDate;
            endDate = savedCustomDateRange.endDate;
          } else {
            switch (savedDateRangeOption) {
              case 'today':
                startDate = today.toISODate();
                endDate = today.toISODate();
                break;
              case 'this_week':
                startDate = today.startOf('week').toISODate();
                endDate = today.endOf('week').toISODate();
                break;
              case 'last_week':
                startDate = today.minus({ weeks: 1 }).startOf('week').toISODate();
                endDate = today.minus({ weeks: 1 }).endOf('week').toISODate();
                break;
              case 'last_month':
                startDate = today.minus({ months: 1 }).startOf('month').toISODate();
                endDate = today.minus({ months: 1 }).endOf('month').toISODate();
                break;
              default:
                startDate = today.startOf('month').toISODate();
                endDate = today.endOf('month').toISODate();
            }
          }
        } else {
          startDate = today.startOf('month').toISODate();
          endDate = today.endOf('month').toISODate();
        }
      } else {
        startDate = today.startOf('month').toISODate();
        endDate = today.endOf('month').toISODate();
      }
    } else if (this.scheduleViewMode === 'MONTHLY') {
      const firstDayOfMonth = today.startOf('month');
      if (firstDayOfMonth.weekday !== 7) {
        startDate = firstDayOfMonth.minus({ days: firstDayOfMonth.weekday }).toISODate();
      } else {
        startDate = firstDayOfMonth.toISODate();
      }
      endDate = today.endOf('month').toISODate();
    } else if (this.scheduleViewMode === 'WEEK_MON_SUN') {
      // For Monday-Sunday week view
      const currentWeekday = today.weekday; // 1 = Monday, 7 = Sunday
      startDate = today.minus({ days: currentWeekday - 1 }).toISODate(); // Go back to Monday
      endDate = today.plus({ days: 7 - currentWeekday }).toISODate(); // Go forward to Sunday

      // When in Mon-Sun mode, we want the days of the week to always be in order from Monday to Sunday
      this.daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    } else if (this.scheduleViewMode === 'DAY') {
      // DAY view - single day
      startDate = today.toISODate();
      endDate = today.toISODate();
    } else {
      // SEVEN_DAYS view (default "weekly" view that shows 7 days from today)
      startDate = today.toISODate();
      endDate = today.plus({ days: 6 }).toISODate();

      // For SEVEN_DAYS view, reorder the days of the week based on today
      const todayWeekday = today.weekday - 1; // 0-based index for today's weekday (0 = Monday)
      const reorderedDaysOfWeek = [];

      // Start with today's weekday and add the next 6 days
      for (let i = 0; i < 7; i++) {
        const dayIndex = (todayWeekday + i) % 7;
        reorderedDaysOfWeek.push(this.daysOfWeek[dayIndex]);
      }

      this.daysOfWeek = reorderedDaysOfWeek;
    }
    this.selectedPeriodRange = { startDate, endDate };
  }

  async goToNextPeriod(): Promise<void> {
    // The demo calendar fixture only covers today..today+6, so any other period comes
    // back empty. Worse, the demo response resolves synchronously, so the weekly view is
    // never torn down and recreated between isScheduleDataLoaded false→true and never
    // re-emits readinessChange — leaving the skeleton up forever. Prompt instead.
    if (this.demoBlock.block()) {return;}
    this.isScheduleDataLoaded = false;
    this.isWeeklyViewReady = false;
    this.clearPropertiesEvents();
    let startDate, endDate;
    if (this.scheduleViewMode === 'MONTHLY') {
      const firstDayOfMonth = LuxonDateTime.fromISO(this.selectedPeriodRange.endDate).plus({ months: 1 }).startOf('month');
      if (firstDayOfMonth.weekday !== 7) {
        startDate = firstDayOfMonth.minus({ days: firstDayOfMonth.weekday % 7 }).toISODate();
      } else {
        startDate = firstDayOfMonth.toISODate();
      }
      const lastDayOfMonth = firstDayOfMonth.plus({ months: 1 }).minus({ days: 1 });
      endDate = lastDayOfMonth.toISODate();
    } else if (this.scheduleViewMode === 'WEEK_MON_SUN') {
      // For Monday-Sunday week view, move 7 days forward
      startDate = LuxonDateTime.fromISO(this.selectedPeriodRange.startDate)
        .plus({ days: 7 })
        .toISODate();
      endDate = LuxonDateTime.fromISO(this.selectedPeriodRange.endDate)
        .plus({ days: 7 })
        .toISODate();
    } else if (this.scheduleViewMode === 'DAY') {
      // DAY view - move one day forward
      startDate = LuxonDateTime.fromISO(this.selectedPeriodRange.startDate).plus({ days: 1 }).toISODate();
      endDate = startDate;
    } else if (this.scheduleViewMode === 'LIST') {
      // LIST view - navigation not applicable, return early
      return;
    } else {
      // SEVEN_DAYS view (default)
      startDate = LuxonDateTime.fromISO(this.selectedPeriodRange.startDate)
        .plus({ days: 7 })
        .toISODate();
      endDate = LuxonDateTime.fromISO(this.selectedPeriodRange.endDate)
        .plus({ days: 7 })
        .toISODate();
    }
    this.selectedPeriodRange = { startDate, endDate };
    this.isScheduleDataLoaded = false;
    this.isWeeklyViewReady = false;
    this.getScheduleData(this.selectedPeriodRange, this.addressFilter);
  }

  goToPreviousPeriod(): void {
    if (this.demoBlock.block()) {return;}
    this.clearPropertiesEvents();
    let startDate, endDate;
    if (this.scheduleViewMode === 'MONTHLY') {
      const firstDayOfMonth = LuxonDateTime.fromISO(this.selectedPeriodRange.endDate).minus({ months: 1 }).startOf('month');
      if (firstDayOfMonth.weekday !== 7) {
        startDate = firstDayOfMonth.minus({ days: firstDayOfMonth.weekday % 7 }).toISODate();
      } else {
        startDate = firstDayOfMonth.toISODate();
      }
      const lastDayOfMonth = firstDayOfMonth.plus({ months: 1 }).minus({ days: 1 });
      endDate = lastDayOfMonth.toISODate();
    } else if (this.scheduleViewMode === 'WEEK_MON_SUN') {
      // For Monday-Sunday week view, move 7 days backward
      startDate = LuxonDateTime.fromISO(this.selectedPeriodRange.startDate)
        .minus({ days: 7 })
        .toISODate();
      endDate = LuxonDateTime.fromISO(this.selectedPeriodRange.endDate)
        .minus({ days: 7 })
        .toISODate();
    } else if (this.scheduleViewMode === 'DAY') {
      // DAY view - move one day backward
      startDate = LuxonDateTime.fromISO(this.selectedPeriodRange.startDate).minus({ days: 1 }).toISODate();
      endDate = startDate;
    } else if (this.scheduleViewMode === 'LIST') {
      // LIST view - navigation not applicable, return early
      return;
    } else {
      // SEVEN_DAYS view (default)
      startDate = LuxonDateTime.fromISO(this.selectedPeriodRange.startDate)
        .minus({ days: 7 })
        .toISODate();
      endDate = LuxonDateTime.fromISO(this.selectedPeriodRange.endDate)
        .minus({ days: 7 })
        .toISODate();
    }
    this.selectedPeriodRange = { startDate, endDate };
    this.isScheduleDataLoaded = false;
    this.isWeeklyViewReady = false;
    this.getScheduleData(this.selectedPeriodRange, this.addressFilter);
  }

  clearPropertiesEvents(): void {
    this.schedule.properties = (this.schedule.properties ?? []).map((property) => {
      property.reservations = [];
      property.jobs = [];
      return property;
    });
    this.schedule.imutableProperties = [...this.schedule.properties];
  }

  async getInitialData(shouldClearLocalStorage = true): Promise<void> {
    try {
      this.hasRequests = false;
      this.isRentalClient = localStorage.getItem('isRentalClient') === 'true';
      const getAddressesFromBackend = this.navCtrl.getParam('getAddressesFromBackend');
      await this.getPageData(getAddressesFromBackend, shouldClearLocalStorage);
      this.loaded = true;
    } catch (error) {
      this.handleLoadErrors(error);
    }
  }

  async getAditionalData(): Promise<void> {
    try {
      if (this.isAllAddressesSelected) {
        return;
      }
      if (this.hasAddresses && this.cards?.length) {
        this.checkForTwoCoops();
        this.checkForRequests();
        this.checkIfOnlyReservations();
        this.checkIfOnlyCrafRequests();
        if (this.hasJobs) {
          const messages = await this.getMessages();
          this.buildActivityTimelines(messages);
        }
      }
      const me = await this.me.load();
      this.showContactConciergeCheck(me);
    } catch (error) {
      if (error?.code === 404 || error?.message?.includes("Couldn't find Address with")) {
        this.goToAllAddresses();
      }
      this.handleLoadErrors(error);
    }
  }

  handleLoadErrors(error: any): void {
    const route = window.location.pathname;
    if (!route.includes('schedule')) {
      return;
    }
    const errorMessage = (error.error && error.error.message) ? error.error.message : error.message;
    this.util.showError(errorMessage, 10000);
  }

  parseScheduleData(scheduleData: any, addresses: TidySelectNumberValueModel[]): Properties {
    const allJobs = (scheduleData ?? []).map((address) => address?.jobs).flat();
    const allReservations = (scheduleData ?? []).map((address) => address?.reservations).flat();
    // Inquiries are their own calendar-view layer now; tag them so the shared
    // reservation parser renders them as inquiry cards (state === 'inquiry').
    const allInquiries = (scheduleData ?? []).map((address) => address?.inquiries).flat();
    const allAvailabilityRequests = (scheduleData ?? []).map((address) => address?.availability_requests).flat();
    const properties: Properties = (addresses ?? []).map((address) => {
      if (address?.value === 0 || address?.value === -1) {
        return
      }
      const foundReservations = allReservations.filter((reservation) => reservation?.address_id === address?.value)
      const foundInquiries = allInquiries
        .filter((inquiry) => inquiry?.address_id === address?.value)
        .map((inquiry) => ({ ...inquiry, type: 'inquiry' }))
      const foundAddress = this.addressResponse.find((addressResponse) => addressResponse?.id === address?.value);
      const parsedReservations = this.schedule.parseCalendarReservations([...foundReservations, ...foundInquiries], foundAddress, this.selectedPeriodRange, this.daysOfWeek);
      const foundJobs = allJobs.filter((job) => job?.address_id === address?.value)
      const parsedJobs = this.schedule.parseCalendarJobs(foundJobs, foundAddress);
      const foundAvailabilityRequests = allAvailabilityRequests.filter((request) => request?.address_id === address?.value)
      const availabilityRequests = this.schedule.parseCalendarAvailabilityRequests(foundAvailabilityRequests, foundAddress, this.selectedPeriodRange, this.daysOfWeek);
      const startDateIsInPast = this.checkIfStartDateIsInPast(parsedReservations[0]?.period?.startDate);
      const nextReservationDate = (startDateIsInPast ? parsedReservations[1]?.period?.startDate : parsedReservations[0]?.period?.startDate) || null;
      const nextJobDate = parsedJobs[0]?.period?.startDate || null;
      return {
        id: address?.value,
        name: address?.viewValue,
        address: foundAddress?.address1,
        reservations: parsedReservations,
        filteredReservations: parsedReservations,
        jobs: parsedJobs,
        filteredJobs: parsedJobs,
        availabilityRequests,
        filteredAvailabilityRequests: availabilityRequests,
        nextReservationDate,
        nextJobDate,
        color: foundAddress?.color,
        plans: foundAddress?.plans || [],
        inquiriesCount: this.inquiryCountsByAddress[address?.value] ?? 0
      }
    }).filter((property) => property);
    // Addresses with pending inquiries lead the list (stable partition). Done
    // here because the main load path takes the keepCurrentPropertiesOrder
    // branch, which preserves the parse order rather than calling
    // sortProperties().
    return [
      ...properties.filter((property) => property.inquiriesCount > 0),
      ...properties.filter((property) => !property.inquiriesCount),
    ];
  }

  checkIfStartDateIsInPast(date) {
    const today = DateTime.now().toISODate();
    const isBeforeToday = DateTime.fromISO(date) < DateTime.fromISO(today);
    return isBeforeToday;
  }

  async showLoading(): Promise<HTMLIonLoadingElement> {
    const loading = await this.loadingCtrl.create({
      message: 'Loading..',
      showBackdrop: true,
      backdropDismiss: false,
      mode: 'ios',
      animated: true,
    });

    loading.present();
    return loading;
  }

  async getScheduleData(
    period: Period,
    addresses: TidySelectNumberValueModel[]
  ): Promise<void> {
    try {
      let scheduleData = [];

      if (this.scheduleViewMode === 'MONTHLY' && !this.routeReused) {
        const endMonth = period.endDate.split('-')[1];
        const startMonth = period.startDate.split('-')[1];
        if (endMonth !== startMonth) {
          period.startDate =
            `${period.endDate.split('-')[0]  }-${  endMonth  }-` + `01`;
        }
        // Started after the period is normalized (it's the same object as
        // selectedPeriodRange) but before the calendar requests, so the blocks
        // are already in hand by the time the views render instead of popping
        // in a beat later — and cost no extra wall-clock.
        this.startOwnerBlocksFetch(addresses, period);

        const startDate = LuxonDateTime.fromISO(period.startDate);
        const endDate = LuxonDateTime.fromISO(period.endDate);
        const middleDate = LuxonDateTime.fromISO(
          `${period.startDate.split('-')[0]  }-${  endMonth  }-` + `15`
        );
        const requestParams = [
          {
            startDate: startDate.toISODate(),
            endDate: middleDate.toISODate(),
            scope: 'jobs',
          },
          {
            startDate: middleDate.plus({ days: 1 }).toISODate(),
            endDate: endDate.toISODate(),
            scope: 'jobs',
          },
          {
            startDate: startDate.toISODate(),
            endDate: endDate.toISODate(),
            scope: 'reservations',
          },
          {
            startDate: startDate.toISODate(),
            endDate: endDate.toISODate(),
            scope: 'inquiries',
          },
          {
            startDate: startDate.toISODate(),
            endDate: endDate.toISODate(),
            scope: 'availability_requests',
          },
        ];
        const weeklyDataObservables = requestParams.map((requestParam) =>
          from(
            this.schedule.getScheduleCalendar(
              requestParam.startDate,
              requestParam.endDate,
              this.filters,
              requestParam?.scope
            )
          )
        );
        forkJoin(weeklyDataObservables)
          .subscribe({
            // The loading flags flip only once the owner blocks have been
            // stamped on too, so the calendar renders complete rather than
            // gaining hatched bars a moment after it appears.
            next: (results) => {
              scheduleData = results.flat();
              void this.operationsAfterScheduleDataRetrieved(scheduleData, addresses)
                .catch((error) => this.logger.error(error, 'schedule-data'))
                .finally(() => {
                  this.isScheduleDataLoaded = true;
                  this.isLoadingSchedule = false;
                });
            },
            error: (_error) => {
              this.isScheduleDataLoaded = true;
              this.isLoadingSchedule = false;
            },
          });
      } else {
        this.startOwnerBlocksFetch(addresses, period);
        if (this.scheduleViewMode === 'LIST') {
          // For LIST view, fetch only jobs and availability requests (no reservations)
          const requestParams = [
            {
              startDate: period.startDate,
              endDate: period.endDate,
              scope: 'jobs',
            },
            {
              startDate: period.startDate,
              endDate: period.endDate,
              scope: 'availability_requests',
            },
          ];
          const listDataObservables = requestParams.map((requestParam) =>
            from(
              this.schedule.getScheduleCalendar(
                requestParam.startDate,
                requestParam.endDate,
                this.filters,
                requestParam?.scope
              )
            )
          );
          forkJoin(listDataObservables)
            .subscribe({
              next: (results) => {
                scheduleData = results.flat();
                void this.operationsAfterScheduleDataRetrieved(scheduleData, addresses)
                  .catch((error) => this.logger.error(error, 'schedule-data'))
                  .finally(() => {
                    this.isScheduleDataLoaded = true;
                    this.isLoadingSchedule = false;
                  });
              },
              error: (_error) => {
                this.isScheduleDataLoaded = true;
                this.isLoadingSchedule = false;
              },
            });
        } else {
          scheduleData = this.showAboutThisPage ? this.onboardingDummyData.getScheduleMockData() : await this.schedule.getScheduleCalendar(
            period.startDate,
            period.endDate,
            this.filters
          );
          await this.operationsAfterScheduleDataRetrieved(scheduleData, addresses);
          this.isScheduleDataLoaded = true;
          this.isLoadingSchedule = false;
        }
      }
    } catch (_error) {
      this.isScheduleDataLoaded = true;
      this.isLoadingSchedule = false;
    }
  }

  // Groups the still-unanswered inquiries (all dates) by address. Failures
  // resolve to an empty map so the schedule renders normally without counts.
  private fetchPendingInquiryCounts(): Promise<Record<number, number>> {
    if (!this.pendingInquiryCountsPromise) {
      this.pendingInquiryCountsPromise = firstValueFrom(this.inquiriesService.getInquiries())
        .then((inquiries) => {
          const counts: Record<number, number> = {};
          for (const inquiry of inquiries ?? []) {
            // GET /inquiries returns the whole list regardless of status, so an
            // accepted, declined or expired request would otherwise pad this
            // count. Counting only what the Inquiries page badges as "Needs
            // response" keeps the two surfaces telling the same story —
            // effectiveInquiryStatus also catches the rows the backend has not
            // swept to 'expired' yet, which a raw available_actions check misses.
            if (effectiveInquiryStatus(inquiry) !== 'pending') {
              continue;
            }
            counts[inquiry.address_id] = (counts[inquiry.address_id] ?? 0) + 1;
          }
          return counts;
        })
        .catch(() => ({}));
    }
    return this.pendingInquiryCountsPromise;
  }

  async operationsAfterScheduleDataRetrieved(
    scheduleData: any,
    addresses: TidySelectNumberValueModel[]
  ): Promise<void> {
    this.inquiryCountsByAddress = await this.fetchPendingInquiryCounts();
    if (this.addressResponse?.length === 1) {
      scheduleData?.map((day) => {
        day.jobs?.map((job) => {
          if (job.address_id === this.addressResponse[0].id) {
            this.hasJobsAtOnlyAddress = true;
          }
        });
      });
    }
    const parsedScheduleData = this.keepShownOwnerBlocks(this.parseScheduleData(scheduleData, addresses));
    this.schedule.properties = parsedScheduleData;
    this.schedule.imutableProperties = parsedScheduleData;
    const skipSortingProperties =
      this.storage.retrieve('skipSortingProperties');
    if (!skipSortingProperties) {
      this.sortProperties();
    } else {
      this.keepCurrentPropertiesOrder();
    }
    this.storage.save('skipSortingProperties', false);
    this.updateTableFilters();
    await this.attachOwnerBlocks();
  }

  /**
   * Starts the owner-blocks fetch (one light GET per property against the
   * Listing Builder date-settings store) so it runs concurrently with the
   * schedule-calendar requests instead of after them. The token is bound to
   * this fetch so a later period/view can't have its result attached to an
   * earlier render's properties. Stores `null` when the current view doesn't
   * use blocks, and an empty map on failure — the calendar stays fully usable
   * either way.
   */
  private startOwnerBlocksFetch(addresses: TidySelectNumberValueModel[], period: Period): void {
    // `parseAddressList` prepends the "All Properties" (-1) and appends the
    // "Add Property" (0) pseudo-entries; neither is a real address.
    const addressIds = (addresses ?? []).map((address) => address?.value).filter((id) => id > 0);
    const isRentalClient = localStorage.getItem('isRentalClient') === 'true';
    const viewUsesBlocks = this.scheduleViewMode === 'MONTHLY'
      || this.scheduleViewMode === 'SEVEN_DAYS'
      || this.scheduleViewMode === 'WEEK_MON_SUN';
    // Demo/preview sessions run this path too — OwnerBlocksService serves them
    // sample ranges instead of calling the date-settings store.
    if (!isRentalClient || !viewUsesBlocks || this.showAboutThisPage || addressIds.length === 0) {
      this.fetchingOwnerBlocksKey = null;
      this.ownerBlocksFetchToken++;
      this.ownerBlocksPromise = Promise.resolve(null);
      return;
    }
    const token = ++this.ownerBlocksFetchToken;
    const range = { startDate: period.startDate, endDate: period.endDate };
    this.fetchingOwnerBlocksKey = `${this.scheduleViewMode}|${range.startDate}|${range.endDate}`;
    // Padded a month either side so blocks straddling the visible window still
    // draw their in-window portion. The service widens further on its own when a
    // run reaches the edge, so the ranges it returns are never clipped; the
    // filter below then drops the ones that don't overlap the visible period.
    const dateFrom = LuxonDateTime.fromISO(range.startDate).minus({ days: 31 }).toISODate();
    const dateTo = LuxonDateTime.fromISO(range.endDate).plus({ days: 31 }).toISODate();
    this.ownerBlocksPromise = this.ownerBlocksService.getBlocksForAddresses(addressIds, dateFrom, dateTo)
      .then((ranges) => {
        const blocksByAddress = new Map<number, OwnerBlock[]>();
        ranges
          .filter((block) => block.endDate >= range.startDate && block.startDate <= range.endDate)
          .forEach((block) => {
            const parsed = this.ownerBlocksService.toScheduleBlock(block, range, this.daysOfWeek);
            const existing = blocksByAddress.get(block.addressId) ?? [];
            existing.push(parsed);
            blocksByAddress.set(block.addressId, existing);
          });
        return blocksByAddress;
      })
      .catch((error) => {
        this.logger.error(error, 'schedule-owner-blocks');
        return new Map<number, OwnerBlock[]>();
      })
      .then((blocksByAddress) => {
        // Late arrival (past the render deadline below): stamp it on now so a
        // slow fetch still shows its blocks instead of dropping them.
        if (token === this.ownerBlocksFetchToken && this.ownerBlocksDeadlinePassed) {
          this.applyOwnerBlocks(blocksByAddress);
        }
        return blocksByAddress;
      });
  }

  /**
   * Waits for the in-flight fetch so the calendar renders complete rather than
   * gaining hatched bars a beat later — but only up to a deadline. The blocks
   * come from a different store than the schedule calendar and
   * `getDateSettings` has no timeout, so without this a single slow
   * `address-settings-by-date` request would hold the whole schedule behind
   * the skeleton. Past the deadline we fall back to the old progressive
   * behavior and let `startOwnerBlocksFetch` attach them when they land.
   */
  private async attachOwnerBlocks(): Promise<void> {
    interface RaceResult {
      timedOut: boolean;
      blocks: Map<number, OwnerBlock[]> | null;
    }

    const token = this.ownerBlocksFetchToken;
    const pending = this.ownerBlocksPromise;
    this.ownerBlocksDeadlinePassed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Neither racer can reject — `pending` carries its own .catch and the
    // deadline only ever resolves — so no try/finally is needed here.
    const raced = await Promise.race<RaceResult>([
      pending.then((blocks) => ({ timedOut: false, blocks })),
      new Promise<RaceResult>((resolve) => {
        timer = setTimeout(() => resolve({ timedOut: true, blocks: null }), this.OWNER_BLOCKS_RENDER_DEADLINE_MS);
      }),
    ]);
    clearTimeout(timer);
    // A newer period/view started its own fetch while this one was in flight.
    if (token !== this.ownerBlocksFetchToken) { return; }
    if (raced.timedOut) {
      this.ownerBlocksDeadlinePassed = true;
      return;
    }
    if (raced.blocks === null) { return; }
    this.applyOwnerBlocks(raced.blocks);
  }

  /** Stamps blocks onto the property arrays with fresh references so the
   *  OnPush views pick them up. */
  private applyOwnerBlocks(blocksByAddress: Map<number, OwnerBlock[]>): void {
    this.shownOwnerBlocksKey = this.fetchingOwnerBlocksKey;
    const attach = (properties: Properties): Properties => (properties ?? []).map((property) => ({
      ...property,
      ownerBlocks: blocksByAddress.get(property.id) ?? [],
    }));
    this.schedule.imutableProperties = attach(this.schedule.imutableProperties);
    this.schedule.properties = attach(this.schedule.properties);
  }

  /**
   * On a reload of the period already on screen, carries each property's
   * current blocks onto the freshly parsed ones. Without this, blocking or
   * unblocking dates redraws every property without its blocks for the second
   * or so until the refetch lands.
   */
  private keepShownOwnerBlocks(properties: Properties): Properties {
    if (!this.shownOwnerBlocksKey || this.shownOwnerBlocksKey !== this.fetchingOwnerBlocksKey) {
      return properties;
    }
    const shownBlocks = new Map(
      (this.schedule.imutableProperties ?? []).map((property) => [property.id, property.ownerBlocks])
    );
    return (properties ?? []).map((property) => (
      shownBlocks.get(property.id) ? { ...property, ownerBlocks: shownBlocks.get(property.id) } : property
    ));
  }

  keepCurrentPropertiesOrder() {
    const orderMap = new Map(
      (this.schedule.imutableProperties ?? []).map((prop, index) => [prop.id, index])
    );
    this.schedule.properties.sort(
      (a, b) => orderMap.get(a.id) - orderMap.get(b.id)
    );
    this.schedule.imutableProperties = this.schedule.properties;
  }

  sortProperties(): void {
    if (!this.schedule.properties) {
      return;
    }
    this.schedule.properties = this.schedule.properties.sort((a, b) => {
      // Addresses with pending inquiries first, so landing on the schedule
      // surfaces what's waiting on a reply.
      const inquiryPriority = (b?.inquiriesCount ? 1 : 0) - (a?.inquiriesCount ? 1 : 0);
      if (inquiryPriority !== 0) {
        return inquiryPriority;
      }
      const aNextDate = a?.nextReservationDate || a?.nextJobDate;
      const bNextDate = b?.nextReservationDate || b?.nextJobDate;
      if (!aNextDate && !bNextDate) {
        return 0;
      }
      if (!aNextDate) {
        return 1;
      }
      if (!bNextDate) {
        return -1;
      }
      const aNextDateTime = new Date(aNextDate).getTime();
      const bNextDateTime = new Date(bNextDate).getTime();
      return aNextDateTime - bNextDateTime;
    });
    this.schedule.imutableProperties = this.schedule.properties;
  }

  async getPageData(getAddressesFromBackend, shouldClearLocalStorage = true) {
    this.filters.address_ids = null;
    this.filters.homekeeper_ids = null;
    this.initialAddressFilter = [];
    this.restoreFiltersFromStorage();
    const savedAddressFilter = localStorage.getItem('scheduleAddressFilter');
    let addressSelectedFromDropdown = false;
    if (savedAddressFilter && !this.showAboutThisPage) {
      const addressIds = JSON.parse(savedAddressFilter);
      this.filters.address_ids = addressIds;
      this.initialAddressFilter = addressIds;
      localStorage.removeItem('scheduleAddressFilter');
      addressSelectedFromDropdown = true;
    } else if (savedAddressFilter) {
      localStorage.removeItem('scheduleAddressFilter');
    }
    this.hasAddresses = await this.getAddresses(getAddressesFromBackend);
    this.isAllAddressesSelected = !addressSelectedFromDropdown;
    if (this.isAllAddressesSelected) {
      this.desktopWidthContent = scrollContentWidth.NORMAL;
      this.events.publish('updateMenu', '');
      this.addressId = -1;
      await this.getScheduleData(this.selectedPeriodRange, this.addressFilter);
      return
    } else {
      localStorage.setItem('isAllAddressesSelected', 'false')
      this.events.publish('updateMenu', '');
    }
    this.getHasCreditCardInfo();
    if (this.hasAddresses) {
      this.desktopWidthContent = scrollContentWidth.PORTRAIT;
      this.getPlans();
      this.getClientSettings();
      await this.getCards();
    }
  }


  buildAllTableStartEndDaysArray(startDate) {
    this.allTableStartEndDays = [];
    for (let i = 0; i < 30; i++) {
      if (i !== 0) {
        startDate = startDate.plus({ days: 3 });
      }
      this.allTableStartEndDays.push({
        startDate: startDate.toISODate(),
        endDate: startDate.plus({ days: 2 }).toISODate()
      });
    }
  }

  buildReservationAndJobsHtml(calendar, row, header) {
    const addressId = row['<div style="margin-left: -15px">Property</div>'].id;
    const jobs = [];
    const reservations = [];
    calendar.map((day) => {
      day.jobs.map((job) => {
        if (job.address_id === addressId) {
          jobs.push(job);
        }
      });
      day.reservations.map((reservation) => {
        if (reservation.address_id === addressId) {
          reservation['date'] = day.date;
          reservations.push(reservation);
        }
      });
      (day.inquiries || []).map((inquiry) => {
        if (inquiry.address_id === addressId) {
          inquiry['date'] = day.date;
          inquiry['type'] = 'inquiry';
          reservations.push(inquiry);
        }
      });
    });
    const jobsString = this.buildJobsString(jobs, header);
    const reservationsString = this.buildReservationsString(reservations, header);
    let string = jobsString + reservationsString;
    if (string === '') {
      return '<div style="text-align: left !important">-</div>';
    } else {
      string = string.replace(/<br>\s*$/, "");
      return string;
    }
  }

  buildJobsString(jobs, header) {
    let string = '';
    jobs.map((job) => {
      if (`<div style="margin-left: -15px">${job.date}</div>` === header) {
        const startTime = LuxonDateTime.fromISO(`1970-01-01T${job.start_time}:00.000`);
        const formattedTime = startTime.toFormat('h:mm a');
        const jobLink = this.getJobLink(job);
        string += `
        <div class="vertical-align-middle" style="text-align: left !important">
          <img style="width: 16px; margin-right: 5px; margin-bottom: -3px" src="${job.service_icon_url}"><a style="color: #00AABA" href="${jobLink}">${formattedTime}</a> - ${job.job_progress}
        `;
        if (job?.open_issue_count > 0) {
          string += '<img style="width: 16px; margin-right: 5px; margin-bottom: -3px" src="assets/img/alert.svg">';
        }
        string += '</div><br>'
      }
    });
    return string;
  }

  getJobLink(job) {
    if (job?.job_progress === 'completed' || job?.job_progress === 'client_cancelled' || job?.job_progress === 'homekeeper_cancelled') {
      return `/past-job/${job.id}`;
    } else {
      return `/job/${job.address_id}/${job.id}`;
    }
  }

  buildReservationsString(reservations, header) {
    let string = '';
    reservations.map((reservation) => {
      if (`<div style="margin-left: -15px">${reservation.date}</div>` === header) {
        const startDate = new ParseDateTimePipe().transform(reservation.check_in_date, 'MMM d');
        const endDate = new ParseDateTimePipe().transform(reservation.check_out_date, 'MMM d');
        string += `
        <div class="vertical-align-middle" style="color: #666; text-align: left !important">
          <img style="width: 16px; margin-right: 5px; margin-bottom: -3px" src="assets/img/calendar-link.svg">${startDate} - ${endDate}
        </div><br>
        `;
      }
    });
    return string;
  }

  async goToPropertyPage(addressId) {
    if (this.showAboutThisPage) {
      return;
    }
    const isMobileResolution = !this.windowService.isDesktopRes;
    this.currentAddress.addressId = addressId.toString();
    localStorage.setItem('isAllAddressesSelected', 'false');
    if (isMobileResolution) {
      await this.storage.save('dialog-right-side-open', false)
      this.navCtrl.navigateForward('schedule-list');
    } else {
      await this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(ScheduleListPage);
    }
  }

  async getClientSettings() {
    this.phoneNumber = await this.storage.retrieve('phoneNumber');
    if (this.phoneNumber) {
      this.hasPhone = this.phoneNumber !== null && this.phoneNumber !== '';
    } else {
      this.settings = await this.client.getClientSettings();
      this.phoneNumber = this.settings?.profile?.phone;
      this.hasPhone = this.settings?.profile?.phone === null || this.settings?.profile?.phone === '';
    }
    if (!this.showAboutThisPage) {
      this.hasPrivatePro = await this.pros.checkIfHasPrivatePro();
    }
  }

  async getAddresses(getAddressesFromBackend) {
    if (this.showAboutThisPage) {
      this.addressResponse = this.onboardingDummyData.getPropertiesMockData();
    } else {
      this.addressResponse = await this.client.getMoreDetailAddresses(getAddressesFromBackend);
    }
    if (this.addressResponse.length === 0){
      localStorage.setItem('getAddressesFromBackend', 'true');
      return false;
    }
    else {
      if (this.showAboutThisPage) {
        this.addressId = this.onboardingDummyData.getPropertiesMockData()[0].id;
      } else {
        this.addressId = await this.client.getSelectedAddressId(this.addressResponse);
      }
      this.addressFilter = this.client.parseAddressList(this.addressResponse, true);
      if (this.addressResponse.length > 1 || this.isRentalClient) {
        const allPropertiesOption = {
          value: -1,
          viewValue: new TranslationPipe().transform('All Properties'),
        }
        this.addressFilter.unshift(allPropertiesOption);
      }
      this.addressFilterLoaded = true;
      this.getSelectedAddressInfo();
      return true;
    }
  }

  getSelectedAddressInfo() {
    const selectedAddress = this.selectedAddress();
    localStorage.setItem('timezone', selectedAddress?.timezone);
    this.addressRegionId = selectedAddress?.region_id;
    this.addressName = this.client.buildAddressName(selectedAddress);
  }

  async getHasCreditCardInfo() {
    const address = this.selectedAddress();
    this.automaticBookingEnabled = address?.address_automatic_booking_setting?.automatic_booking_enabled;
    if (this.automaticBookingEnabled) {
      this.hasCreditCard = await this.card.hasValidCard();
    }
  }

  getPlans() {
    let plans;
    this.addressResponse.map((address) => {
      if (address.id === this.addressId) {
        plans = address.plans;
      };
    });
    this.plans = plans === null ? [] : plans;
    this.checkIfHasAllOneTimePlans();
  }

  checkIfHasAllOneTimePlans() {
    this.hasAllOneTimePlans = true;
    this.plans.map((plan) => {
      if (plan?.plan_frequency !== 'once') {
        this.hasAllOneTimePlans = false;
      }
    });
  }

  async getCards() {
    this.cardsLoaded = false;
    const cards = await this.schedule.getCards(this.addressId);
    //TODO https://trello.com/c/70YyrgSM/7701-b-list-endpoint-add-unassigned-data-to-the-list-response
    //Have BE return the data from getUpdates for each card instead of FE running this method
    cards.map(async (card) => {
      if (card?.template_data?.scenario_type === 'unassigned' || card?.template_data?.scenario_type === 'manual_request') {
        let _updateId;
        card?.template_data?.updates.map(async (update) => {
          let updateHasAction;
          update?.text?.map((text) => {
            updateHasAction = text?.action === 'cleaning_update_page';
          });
          if (updateHasAction) {
            card.template_data['unassigned'] = await this.schedule.getUpdates(update.id);
          };
        });
      };
    });
    if (this.automaticBookingEnabled) {
      const today = LuxonDateTime.now();
      let addedFourWeekAlert = false;
      cards.map(card => {
        if (card?.template === 'guest_reservations') {
          const reservationDate = LuxonDateTime.fromISO(card?.template_data?.check_out_date);
          const isFourWeeksOut = reservationDate.minus({ weeks: 4 }).startOf('day') >= today.startOf('day');
          if (isFourWeeksOut && !addedFourWeekAlert) {
            card['fourWeekAlert'] = true;
            addedFourWeekAlert = true;
          }
        }
      });
    }
    cards.map((card) => {
      if (card.template_data?.notification_history) {
        card.template_data.notification_history = this.schedule.getFilteredWaterfallHistory(card.template_data.notification_history);
      }
    })
    this.cards = cards;
    this.updateJobRequestStatus();
    this.hasJobs = !!this.cards?.find((card) => card?.template !== 'guest_reservations');
    this.cardsLoaded = true;
  }

  async getMessages() {
    const jobIdsArray = [];
    this.cards?.map((card) => {
      if (card?.template === 'cleaning') {
        jobIdsArray.push(card?.template_data?.job?.id);
      }
    })
    const messages = await this.communication.getJobMessages(jobIdsArray);
    return messages;
  }

  buildActivityTimelines(messages) {
    this.cards?.map((card) => {
      if (card?.template === 'cleaning') {
        const homekeeperJobId = card?.template_data?.activity_timelines?.[0]?.homekeeper_job_id;
        const homekeeperJobMessages = messages?.filter((message) => message?.metadata?.homekeeper_job_id === homekeeperJobId);
        const updates = card?.template_data?.activity_timelines[0]?.activity_timeline_items?.filter((item) => item.type === 'cleaning_update');
        if (homekeeperJobMessages?.length > 0) {
          if (card?.template_data?.activity_timelines[0]) {
            card.template_data.activity_timelines[0]['activity_timeline_items'] = this.concatenateMessagesAndUpdates(updates, homekeeperJobMessages);
          } else {
            card?.template_data?.activity_timelines?.push({
              activity_timeline_items: homekeeperJobMessages
            });
          }
        }
      }
    });
  }

  concatenateMessagesAndUpdates(updates, messages) {
    updates?.map((update) => {
      update['sent_at'] = update?.data?.created_at;
    });
    if (updates?.length) {
      const activity = updates?.concat(messages);
      activity?.sort((a, b) => a?.sent_at < b?.sent_at ? 1 : -1);
      return activity;
    } else {
      return messages;
    }
  }

  checkForTwoCoops() {
    if (this.cards?.length > 1) {
      const numberOfPendingCoops = this.cards?.filter((card) => card?.template === 'job_request');
      this.twoCoops = (numberOfPendingCoops?.length > 1) ? true : false;
    }
  }

  checkForRequests() {
    this.cards?.map((card) => {
      if (card?.template === 'pending_availability_request') {
        this.hasRequests = true;
      }
    })
  }

  checkIfOnlyReservations() {
    this.hasReservations = !!this.cards?.find((card) => card?.template === 'guest_reservations');
    this.hasOnlyReservations = !this.hasJobs && this.hasReservations;
  }

  checkIfOnlyCrafRequests() {
    const hasJobs = !!this.cards?.find((card) => card?.template !== 'pending_availability_request');
    const hasCrafRequests = !!this.cards?.find((card) => card?.template === 'pending_availability_request');
    this.hasOnlyCrafRequests = !hasJobs && hasCrafRequests;
  }

  async showContactConciergeCheck(me) {
    try {
      this.clientName = me?.customer_account?.name;
      if (!this.isRentalClient) {
        return;
      }
      const hasPastJobsStorage = localStorage.getItem('hasPastJobs');
      if (hasPastJobsStorage !== null) {
        this.showContactConcierge = hasPastJobsStorage === 'false';
      } else {
        const pastJobs = await this.client.getCleaningsHistory();
        const hasPastJobs = pastJobs?.length > 0;
        localStorage.setItem('hasPastJobs', hasPastJobs ? 'true' : 'false');
        this.showContactConcierge = !hasPastJobs;
      }
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  updateJobRequestStatus() {
    this.cards.map((card) => {
      if (card?.template_data?.notification_history) {
        if (card?.template_data?.notification_history[0]?.text === 'All pros on your priority list have declined.') {
          card.template_data.job_request_status = 'failed';
        }
      }
    });
  }

  applyListToJob(job) {
    const params = {
      job,
    }
    this.navCtrl.navigateForward('apply-to-job', params);
  }

  goToEditBackupTimes(job) {
    const params = {
      job
    }
    this.navCtrl.navigateForward('job-backup-times', params);
  }

  learnMoreSdc() {
    this.util.openUrl('https://help.tidy.com/add-or-request-a-job');
  }

  async bookJob(bookingType) {
    if (this.showAboutThisPage) {
      return;
    }
    await this.storage.save('bookJobBackPage', 'schedule');
    await this.client.getClientInfo();
    localStorage.setItem('region_id', this.addressRegionId.toString());
    const address = this.selectedAddress() || this.addressResponse[0];
    const isMobileResolution = !this.windowService.isDesktopRes;
    const params = {
      address,
      bookingType,
    }
    await this.bookService.goToBookJob(params);
  }

  async goToPlansPage() {
    if (this.showAboutThisPage) {
      return;
    }
    const params = {
      address: this.selectedAddress(),
      plans: this.plans,
      addressFilter: this.addressFilter,
      addressResponse: this.addressResponse,
      cards: this.cards
    }
    this.addressId = null;
    await this.navCtrl.navigateForward('plans', params);
  }

  async goToPlansForProperty(propertyId: number): Promise<void> {
    if (this.showAboutThisPage || !propertyId) {
      return;
    }
    const address = this.addressResponse.find((ad) => ad.id === propertyId);
    if (!address) {
      return;
    }
    const params = {
      address,
      plans: address.plans || [],
      addressFilter: this.addressFilter,
      addressResponse: this.addressResponse,
      cards: this.cards,
    };
    if (!this.windowService.isDesktopRes) {
      await this.navCtrl.navigateForward('plans', params);
      return;
    }
    await this.storage.save('dialog-right-side-open', true);
    await this.storage.save('dialog-params', params);
    this.rightSidePanelService.openRightSidePanel(PlansPage);
  }

  rescheduleCancel(card) {
    const address = this.selectedAddress();
    const bookingId = card.template_data.booking.id;
    const bookingKey = card?.template_data.job?.service_type_details?.key || card?.template_data.booking?.bookable_service?.key;
    this.navCtrl.navigateForward('reschedule-cancel', { address, card, bookingId, bookingKey });
  }

  async editProRequest(availabilityRequest: AvailabilityRequestResponse) {
    this.addressId = parseInt(localStorage.getItem('addressId'));
    try {
      const isMobileResolution = !this.windowService.isDesktopRes;
      const params = {
        cameFromEditRequest: true,
        availabilityRequestId: availabilityRequest.id,
        requestTimes: availabilityRequest.request_times,
        serviceType: availabilityRequest.service_type_details?.key || availabilityRequest.booking?.bookable_service?.key,
        times: availabilityRequest.request_times,
        frequency: {
          viewValue: availabilityRequest.frequency,
          value: availabilityRequest.frequency
        },
        pro: {
          first_name: availabilityRequest.homekeeper?.name.split(' ')[0],
          id: availabilityRequest.homekeeper?.id
        },
        address: this.selectedAddress()
      };
      if (!isMobileResolution) {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(PendingProRequestPage);
      } else {
        this.navCtrl.navigateForward('pending-pro-request', params);
      }
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  async cancelProRequest(availabilityRequest) {
    const params: ConfirmPageParamsModel = {
      title: 'Cancel Request?',
      body: 'You can always create a new request if you\'d like.',
      backText: 'Go Back',
      confirmText: 'Cancel Request',
      confirmAction: this.confirmCancelProRequest.bind(this, availabilityRequest)
    }
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await confirmationModal.present();
  }

  async confirmCancelProRequest(availabilityRequest) {
    try {
      await this.schedule.cancelCrafRequest(availabilityRequest.id);
      await this.getCards();
      this.checkForRequests();
      this.modalCtrl.dismiss();
    } catch(err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    }
  }

  async cancelWaterfallRequest(jobId) {
    const params: ConfirmPageParamsModel = {
      title: 'Cancel Request?',
      body: 'You can always create a new request if you\'d like.',
      backText: 'Go Back',
      confirmText: 'Cancel Request',
      confirmAction: this.confirmCancelWaterfallRequest.bind(this, jobId)
    };
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await confirmationModal.present();
  }

  async confirmCancelWaterfallRequest(bookingId) {
    try {
      await this.schedule.cancelJob(bookingId);
      const getAddressesFromBackend = true;
      await this.getPageData(getAddressesFromBackend);
      this.modalCtrl.dismiss();
    } catch(err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    }
  }

  goToRequestProCounter(availabilityRequest: AvailabilityRequestResponse) {
    const params = {
      planFrequency: availabilityRequest.frequency,
      service: availabilityRequest.service_type_details,
      proposedTimes: availabilityRequest.request_times.map(day => ({...day, times: day.requested_times})),
      proName: availabilityRequest.homekeeper?.name.split(' ')[0],
      availabilityRequestId: availabilityRequest.id,
      addressId: this.addressId,
      addressName: this.addressName
    };
    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.storage.save('dialog-params', params);
      this.rightSidePanelService.openRightSidePanel(RequestProCounterPage);
    } else {
      this.navCtrl.navigateForward('request-pro-counter', params);
    }
  }

  contactPro(card) {
    const params = {
      pro: card?.template_data?.homekeepers[0],
      isPrivate: card.template_data.job.is_private,
      clientPhone: this.phoneNumber
    }
    const url = 'call-pro';
    const component = CallProPage;
    this.rightSidePanelService.navigateTo(url, params, component);
  }

  goToProPage(proId) {
    localStorage.setItem('myProBackPage', 'schedule');
    this.navCtrl.navigateForward(`my-pro/${proId}`);
  }

  @Loading('', true)
  async checkWaterfallHistory(selectionChange, card){
    const notification = card?.template_data?.notification_history[selectionChange.selectedIndex];
    this.notificationHistory = '';
    if(notification.type === 'history' && (notification?.data?.id && notification?.data?.unassigned_job_id)){
      try {
        this.notificationHistory = await this.client.getWaterfallNotificationHistory(notification?.data?.id);
      } catch (err) {
        this.notificationHistory = '';
        this.logger.error(err);
      }
    } else {
      this.notificationHistory = '';
    }
  }

  goToNotificationDetailsPage(notification) {
    const params = {
      notification
    }
    this.navCtrl.navigateForward('notification-details', params);
  }

  goToEditContactInfo() {
    const params = {
      settings: this.settings
    };
    this.navCtrl.navigateForward('contact-info', params);
  }

  async goToJobPage(card) {
    // Job detail pages aren't populated with demo data — clicking a demo job would land
    // on a broken page, so prompt to create an account instead.
    if (this.demoBlock.block()) {return;}
    const address = this.selectedAddress();
    const params = {
      card,
      address,
      addressName: this.addressName,
      settings: this.settings,
      cameFromSchedulePage: true,
      clientName: this.clientName
    };
    localStorage.setItem('jobBackPage', 'schedule');
    this.navCtrl.navigateForward(`job/${address.id}/${card.template_data.job.id}`, params);
  }

  needsWork(job, pro) {
    const params = {
      firstName: pro.first_name,
      pro,
      isCleaningJob: job.template_data.job.service_type_details.service_category === 'Regular Cleaning' || job.template_data.booking.bookable_service.service_category.name === 'Regular Cleaning'
    }
    this.navCtrl.navigateForward(`needs-work-what-happened/${job.template_data.job.id}/${pro.homekeeper_job_id}`, params);
  }

  loveIt(job, pro) {
    this.navCtrl.navigateForward(`love-it/${job.template_data.job.id}/${pro.homekeeper_job_id}`);
  }

  reportNoShow(proName, jobId, hkId) {
    const params = {
      proName,
      hkId,
      jobId
    }
    this.navCtrl.navigateForward('pro-did-not-show-up', params);
  }

  async skipJob(bookingId) {
    const params: ConfirmPageParamsModel = {
      title: 'Skip Job?',
      body: 'This will cancel the job (does not affect other jobs).',
      backText: 'Go Back',
      confirmText: 'Skip Job',
      confirmAction: this.skipJobAction.bind(this, bookingId)
    }

    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal',
    });

    await confirmationModal.present();
  }

  async skipJobAction(bookingId) {
    try {
      await this.schedule.cancelJob(bookingId);
      const getAddressesFromBackend = true;
      await this.getPageData(getAddressesFromBackend);
      this.modalCtrl.dismiss();
    } catch(err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    }
  }

  async rescheduleJob(card) {
    const address = this.selectedAddress();
    const bookingType = card.template_data?.job?.allowed_actions?.includes('reschedule_booking') ? 'reschedule_job' : 'add_one_time_job';
    const planId = card.template_data.booking.plan_id;
    const jobId = card.template_data.job.id;
    const bookingId = card.template_data.booking.id;
    await this.bookService.goToBookJob({ address, bookingType, jobId, bookingId, planId });
  }

  requestSubstitute(jobId) {
    this.navCtrl.navigateForward(`request-substitute/${jobId}`);
  }

  async keepOnlyOnePro(jobId) {
    const params: ConfirmPageParamsModel = {
      title: 'Keep Only 1 Pro?',
      body: 'You\ll be billed the prorated rate.',
      backText: 'Go Back',
      confirmText: 'Confirm',
      confirmAction: this.confirmKeepOnePro.bind(this, jobId)
    }
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await confirmationModal.present();
  }

  async confirmKeepOnePro(jobId) {
    try {
      await this.schedule.keepOneProForTwoProJob(jobId);
      const successParams = {
        header: 'Kept One Pro',
        body: 'You were billed the prorated rate. Please contact the concierge with any questions.',
        buttonText: 'Ok',
        buttonRoute: 'schedule'
      };
      this.navCtrl.navigateForward('success', successParams);
    } catch(err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    }
  }

  selectedAddress() {
    return this.addressResponse.find(ad => ad.id === this.addressId);
  }

  goToReviews() {
    this.util.openUrl('https://www.tidy.com/reviews');
  }

  goToReservation(event) {
    if (this.demoBlock.block()) {return;}
    let guestReservation = event?.event?.object
    const didClickReservationFromAddressView = !guestReservation;
    this.addressId = event?.property?.id;
    this.addressName = event?.property?.name;
    if (didClickReservationFromAddressView) {
      guestReservation = event?.template_data;
      guestReservation['id'] = event.id.replace('guest_reservation-', '');
      this.addressId = event?.template_data?.address?.id;
      this.addressName = event?.template_data?.address?.address1;
    }
    const params = {
      guestReservation,
      address: this.addressName,
      addressId: event?.template_data?.address?.id || event?.event?.address?.id
    };
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      this.rightSidePanelService.setDialogPageTitle('Reservation');
      this.storage.save('dialog-params', params);
      this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(ReservationPage);
    } else {
      this.navCtrl.navigateForward('reservation', params);
    }
  }

  goToTerms() {
    this.util.openUrl('https://www.tidy.com/legal/property-managers-customer');
  }

  learnMoreReservations() {
    this.util.openUrl('https://help.tidy.com/integrations');
   }

  goToScheduleCallPage() {
    this.util.openUrl('https://www.tidy.com/schedule-integration-support');
  }

  goToIntegrations() {
    if (this.showAboutThisPage) {
      return;
    }
    const address = this.addressResponse.find((addr) => addr.id === this.addressId);
    const params = {
      address
    }
    this.navCtrl.navigateForward('automatic-booking-property', params);
  }

  goToShareJobPage(job) {
    const params = {
      job,
      addressName: this.addressName
    };
    this.navCtrl.navigateForward('share-job', params);
  }

  showUpdates(card) {
    return card?.template_data?.activity_timelines[0]?.activity_timeline_items?.length && !card?.template_data?.unassigned;
  }

  async goToChangePro(card) {
    if (this.showAboutThisPage) {
      return;
    }
    try {
      const privateProsList = await this.pros.getAllPrivatePros();
      const params = {
        job: card,
        addressId: this.addressId,
        privateProsList,
        booking: card.template_data.booking
      }
      const url = privateProsList?.length ? 'assign-pro' : 'why-add-private-pros';
      this.navCtrl.navigateForward(url, params);
    } catch(err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  goToAddAddress(){
    if (this.showAboutThisPage) {
      return;
    }
    this.navCtrl.navigateForward('add-property');
  }

  goToBookDemo() {
    this.util.openUrl('https://www.tidy.com/demo');
  }

  async handleClickActionMonthlyView(event: onClickActionMonthlyView): Promise<void> {
    if (event?.job?.parsedRequestTimes) {
      const loading = await this.showLoading();
      this.addressId = event?.job.address?.id;
      const availabilityRequest: AvailabilityRequestResponse = event?.job?.object;
      if (availabilityRequest.status === 'countered') {
        this.goToRequestProCounter(availabilityRequest);
      } else {
        this.editProRequest(availabilityRequest);
      }
      loading.dismiss();
      return;
    }
    if (event.type === 'job') {
      this.goToCleaningDetail(event.job, event.job.address.id);
    }
    if (event.type === 'inquiry') {
      // Same destination as the weekly view's inquiry pill.
      this.navCtrl.navigateForward('inquiries');
      return;
    }
    if (event.type === 'day' || event.type === 'reservation') {
      const dialogParams = await this.storage.retrieve('dialog-params');
      const params = { ...dialogParams, ...event };
      this.storage.save('dialog-params', params);
      const isMobileResolution = !this.windowService.isDesktopRes;
      if (!isMobileResolution) {
        this.storage.save('dialog-right-side-open', true);
        this.rightSidePanelService.openRightSidePanel(SummaryOfDayPage);
      } else {
        this.storage.save('dialog-right-side-open', false);
        this.navCtrl.navigateForward('summary-of-day', params);
      }
    }
  }

  async handleRequestMoreData(event: {startDate: string, endDate: string}): Promise<void> {
    try {
      // Create a temporary period range for the requested batch
      const batchPeriodRange = {
        startDate: event.startDate,
        endDate: event.endDate
      };

      // For LIST view, fetch only jobs and availability requests (no reservations)
      if (this.scheduleViewMode === 'LIST') {
        const requestParams = [
          {
            startDate: event.startDate,
            endDate: event.endDate,
            scope: 'jobs',
          },
          {
            startDate: event.startDate,
            endDate: event.endDate,
            scope: 'availability_requests',
          },
        ];

        let batchData = [];
        for (const requestParam of requestParams) {
          const data = await this.schedule.getScheduleCalendar(
            requestParam.startDate,
            requestParam.endDate,
            this.filters,
            requestParam.scope
          );
          batchData = batchData.concat(data);
        }

        // Merge the new data with existing properties
        this.mergeScheduleData(batchData);
      } else {
        // Fetch data for the specific batch (default behavior)
        const batchData = await this.schedule.getScheduleCalendar(
          event.startDate,
          event.endDate,
          this.filters
        );

        // Merge the new data with existing properties
        this.mergeScheduleData(batchData);
      }

    } catch (error) {
      console.error('Error fetching more data:', error);
    }
  }

  mergeScheduleData(newData: any[]): void {
    // Merge new data with existing schedule properties
    if (newData && newData.length > 0) {
      // Prepend new data (past data) to existing properties
      void this.operationsAfterScheduleDataRetrieved(newData, this.addressFilter)
        .catch((error) => this.logger.error(error, 'schedule-data'));
    }
  }

  async handleClickAction(event: ClickAction): Promise<void> {
    this.bookJobService.clearBookJobStorage();
    if (event.eventType === 'availabilityRequests') {
      const loading = await this.showLoading();
      this.addressId = event?.property?.id;
      const availabilityRequest: AvailabilityRequestResponse = event?.event?.object;
      if (availabilityRequest.initiated_by === 'homekeeper') {
        this.goToRequestProCounter(availabilityRequest);
      } else {
        this.editProRequest(availabilityRequest);
      }
      loading.dismiss();
      return
    }
    if (event?.target === 'property') {
      this.goToPropertyPage(event?.property?.id);
      return
    }
    if (event?.target === 'inquiries') {
      this.navCtrl.navigateForward('inquiries');
      return
    }
    if (event?.target === 'plan') {
      await this.goToPlansForProperty(event?.property?.id);
      return
    }
    if (event?.eventType === 'jobs') {
      this.goToCleaningDetail(event.event as Job, event.property.id);
      return
    }
    if (event?.eventType === 'reservations') {
      const loading = await this.showLoading();
      this.goToReservation(event);
      loading.dismiss();
      return
    }
    const loading = await this.showLoading();
    this.goToReservation(event);
    loading.dismiss();
  }

  @Loading('', true)
  async goToCleaningDetail(cleaning: any, addressId: number) {
    if (this.demoBlock.block()) {return;}
    // Sample jobs shown under "About this page" don't exist: opening one asks the API
    // for a sample booking (403) and the job panel never loads. Like the page's other
    // sample-mode actions, a click does nothing until the prompt is dismissed.
    if (this.showAboutThisPage) {return;}
    const isMobileResolution = !this.windowService.isDesktopRes;
    const isPastJob = cleaning.status.name === 'cancelled' || cleaning.status.name === 'completed';
    const isPrivateJob = cleaning.isPrivate;
    if (!isMobileResolution) {
      this.navigateToJobPageInsideDialog(cleaning, addressId, isPastJob, isPrivateJob);
      return;
    }
    localStorage.setItem('pastJobBackPage', 'schedule');
    if (isPastJob) {
      if (isPrivateJob) {
        this.navCtrl.navigateForward([`past-private-job/${cleaning.id}`]);
      } else {
        this.navCtrl.navigateForward([`past-job/${cleaning.id}`]);
      }
    } else {
      this.navCtrl.navigateForward([`job/${addressId}/${cleaning.id}`], { object: cleaning.object });
    }
  }

  navigateToJobPageInsideDialog(cleaning: Job, addressId: number, isPastJob: boolean, isPrivateJob: boolean): void {
    const params = {
      jobId: cleaning.id,
      addressId,
      ...cleaning,
    }
    this.storage.save('dialog-params', params);
    this.storage.save('dialog-right-side-open', true);
    if (isPastJob && isPrivateJob) {
      this.rightSidePanelService.openRightSidePanel(PastPrivateJobPage);
    } else if (isPastJob) {
      this.rightSidePanelService.openRightSidePanel(PastJobPage);
    } else {
      this.rightSidePanelService.openRightSidePanel(JobPage);
    }
  }

  goToAllAddresses(): void {
    localStorage.setItem('isAllAddressesSelected', 'true');
    location.reload();
  }

  async onFABButtonClick() {
    if (this.showAboutThisPage) {
      return;
    }
    const isRentalClient = localStorage.getItem('isRentalClient') === 'true';
    if (isRentalClient) {
      // Rental clients can add more than jobs (reservations, inquiries,
      // blocked dates), so the + opens the chooser instead of jumping
      // straight into booking.
      return this.showAddTypeSelectionModal();
    }
    if (!this.isAllAddressesSelected) {
      this.bookJob('add_one_time_job');
    } else if (!this.hasPrivatePro) {
      const bookingType = this.addressResponse[0].plans.length > 0 ? 'add_one_time_job' : 'add_job';
      return this.bookJob(bookingType);
    } else {
      this.bookJobSelectAddress();
    }
  }

  async bookJobSelectAddress() {
    this.storage.save('bookJobBackPage', 'schedule');
    await this.bookService.goToBookJob({});
  }

  goToAllIntegrations() {
    this.navCtrl.navigateForward('integration/all')
  }

  goToDashboard() {
    this.navCtrl.navigateForward('properties');
  }

  @Loading('', true)
  async handleAssignedPro(_job) {
    const assignProError = localStorage.getItem('assignProError') === 'true';
    if (!assignProError) {
      const getAddressesFromBackend = this.navCtrl.getParam('getAddressesFromBackend');
      await this.getPageData(getAddressesFromBackend);
      const proName = localStorage.getItem('proName');
      const successMessage = `Successfully assigned ${  proName  }!`;
      this.util.showSuccess(successMessage);
    }
    localStorage.setItem('proName', '');
    localStorage.setItem('assignProError', 'false');
    this.bookJobService.isAssigningPrivatePro = '';
  }

  async showAddTypeSelectionModal(preselectedDate?: string, preselectedProperty?: number) {
    const buttons = [
      {
        text: 'Add Job',
        icon: 'construct-outline',
        handler: () => {
          const isRentalClient = localStorage.getItem('isRentalClient') === 'true';
          if (!this.isAllAddressesSelected) {
            this.bookJob('add_one_time_job');
          } else if (!isRentalClient && !this.hasPrivatePro) {
            const bookingType = this.addressResponse[0].plans.length > 0 ? 'add_one_time_job' : 'add_job';
            return this.bookJob(bookingType);
          } else {
            this.bookJobSelectAddress();
          }
        }
      },
      {
        text: 'Add Inquiry',
        icon: 'help-circle-outline',
        handler: () => {
          this.openAddInquiry(preselectedDate, preselectedProperty);
        }
      },
      {
        text: 'Add Reservation',
        icon: 'calendar-outline',
        handler: () => {
          this.openAddReservation(preselectedDate, preselectedProperty);
        }
      },
      {
        text: 'Block Dates',
        icon: 'lock-closed-outline',
        handler: () => {
          this.openAddOwnerBlock(preselectedDate, preselectedProperty);
        }
      },
      {
        text: 'Cancel',
        role: 'cancel',
        cssClass: 'secondary'
      }
    ];

    await this.util.showAlert('Add to Schedule', 'What would you like to add?', true, buttons);
  }

  async openAddInquiry(preselectedDate?: string, preselectedProperty?: number) {
    await this.storage.save('inquiry-mode', true);
    if (preselectedDate) {await this.storage.save('preselected-date', preselectedDate);}
    if (preselectedProperty) {await this.storage.save('preselected-property', preselectedProperty);}

    const isMobileResolution = !this.windowService.isDesktopRes;
    if (isMobileResolution) {
      this.navCtrl.navigateForward('add-reservation');
    } else {
      await this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.setDialogPageTitle('New Inquiry');
      this.rightSidePanelService.openRightSidePanel(AddReservationPage);
    }
  }

  async openAddReservation(preselectedDate?: string, preselectedProperty?: number) {
    await this.storage.save('inquiry-mode', false);
    if (preselectedDate) {await this.storage.save('preselected-date', preselectedDate);}
    if (preselectedProperty) {await this.storage.save('preselected-property', preselectedProperty);}

    const isMobileResolution = !this.windowService.isDesktopRes;
    if (isMobileResolution) {
      this.navCtrl.navigateForward('add-reservation');
    } else {
      await this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.setDialogPageTitle('New Reservation');
      this.rightSidePanelService.openRightSidePanel(AddReservationPage);
    }
  }

  async openAddOwnerBlock(preselectedDate?: string, preselectedProperty?: number) {
    if (preselectedDate) {await this.storage.save('preselected-date', preselectedDate);}
    if (preselectedProperty) {await this.storage.save('preselected-property', preselectedProperty);}

    const isMobileResolution = !this.windowService.isDesktopRes;
    if (isMobileResolution) {
      // Written explicitly (rather than relying on the default) so a stale
      // value left by the optimizer's Block dates link can't send us there.
      await this.storage.save('owner-block-return-page', 'schedule');
      await this.storage.save('dialog-right-side-open', false);
      this.navCtrl.navigateForward('add-owner-block');
    } else {
      await this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.setDialogPageTitle('Block Dates');
      this.rightSidePanelService.openRightSidePanel(AddOwnerBlockPage);
    }
  }

}
